"use client";

import { useTranslations } from "use-intl";

import { GlobeIcon } from "@/components/appIcons";
import { SocialPlatformIcon } from "@/components/social-platform-icon";
import { socialPlatformForUrl } from "@/lib/social-platform-for-url";
import type { SocialPlatform } from "@garimpa/lib/musicTypes";

export interface ExternalLinkGroup {
  key: "official" | "social" | "reference" | "sources";
  links: Array<{ url: string; label: string; title?: string; platform?: SocialPlatform }>;
}

/** Every external link of an entity, grouped, as plain crawlable anchors. */
export function ExternalLinkGroups({ groups }: { groups: ExternalLinkGroup[] }) {
  const t = useTranslations("shared.entityAbout");

  return (
    <div className="space-y-5">
      {groups
        .filter((group) => group.links.length > 0)
        .map((group) => (
          <section key={group.key} aria-labelledby={`links-${group.key}`} className="space-y-2">
            <h3
              id={`links-${group.key}`}
              className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
            >
              {t("linksGroup", { group: group.key })}
            </h3>
            <ul className="flex flex-wrap gap-2">
              {group.links.map((link) => (
                <li key={link.url} className="min-w-0">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.title}
                    className="inline-flex min-h-11 max-w-full items-center gap-2 break-all rounded-full sm:min-h-9 bg-secondary px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:text-primary"
                  >
                    <LinkGlyph url={link.url} platform={link.platform} />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  );
}

function LinkGlyph({ url, platform }: { url: string; platform?: SocialPlatform }) {
  const known = platform ?? socialPlatformForUrl(url);
  if (known) return <SocialPlatformIcon platform={known} size={16} />;
  return <GlobeIcon className="shrink-0 text-[16px] text-muted-foreground" />;
}
